/* eslint-disable prettier/prettier */
/* eslint-disable react/prop-types */
import ArticleIcon from '@mui/icons-material/Article'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import RefreshIcon from '@mui/icons-material/Refresh'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import {
    Box,
    Button,
    Grid,
    InputAdornment,
    Link,
    Paper,
    TextField,
    Typography,
} from '@mui/material'
import IconAction from '../../components/shared/IconAction'
import InlineLoading from '../../components/shared/InlineLoading'
import SearchIcon from '@mui/icons-material/Search'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import {
    assignableTemplates,
    metaStatus,
    sendsAs,
} from '../../helpers/pipelines'
import { subCategoryLabel } from '../../helpers/templateCategories'
import {
    ACCENT,
    BRAND,
    CONTROL,
    ICON,
    LINK,
    RADIUS,
    STATUS,
    SURFACE,
    TEXT,
    tint,
} from '../../theme/tokens'
import { channelMeta, SectionLabel } from './wizardShared'

// Enough to scan; the search narrows the rest (WhatsApp alone has 263 on dev).
const MAX_SHOWN = 60

/**
 * The Create Template page for a channel, as an address for a new tab (the
 * wizard's draft stays in this one). The form reads `?channel=`.
 */
export const createTemplateHref = (commType) =>
    `${
        process.env.PUBLIC_URL || ''
    }/create-template?channel=${encodeURIComponent(commType)}`

// A template's comms status as text: the text-safe STATUS foregrounds
// (pairs on every card ground in theme/contrast.studio.test.js).
export const STATUS_STYLE = {
    approved: { color: STATUS.success.fg, label: 'Approved' },
    approval_awaited: { color: STATUS.warning.fg, label: 'Pending' },
    disapproved: { color: STATUS.error.fg, label: 'Rejected' },
}

const StatusTag = ({ status }) => {
    const s = STATUS_STYLE[status] ?? { color: TEXT.faint, label: status }
    return (
        <Typography
            component="span"
            variant="overline"
            sx={{
                lineHeight: 1.4,
                color: s.color,
            }}
        >
            {s.label}
        </Typography>
    )
}

// Meta's status as text: green approved, amber waiting, red refused, and
// neutral when none is recorded. Text colours on the card grounds are in the
// contrast test.
export const META_TONE_COLOR = {
    ok: STATUS.success.fg,
    waiting: STATUS.warning.fg,
    blocked: STATUS.error.fg,
    none: TEXT.faint,
}

/** Meta's approval status of a WhatsApp template, as a small tag. */
const MetaTag = ({ meta }) => (
    <Typography
        component="span"
        variant="overline"
        sx={{
            lineHeight: 1.4,
            fontWeight: 700,
            color: META_TONE_COLOR[meta.tone],
        }}
    >
        {meta.label}
    </Typography>
)

const ChannelTab = ({ comm, active, onClick }) => {
    const meta = channelMeta(comm.comm_type)
    return (
        <Box
            role="tab"
            tabIndex={0}
            aria-selected={active}
            aria-label={`${meta.label} templates`}
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onClick()
                }
            }}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.75,
                py: 0.9,
                borderRadius: `${RADIUS.pill}px`,
                cursor: 'pointer',
                outline: 'none',
                border: `1.5px solid ${
                    active ? ACCENT.border55 : SURFACE.border
                }`,
                background: active ? ACCENT.bg08 : SURFACE.subtle,
                transition: 'all 0.15s',
                userSelect: 'none',
                '&:hover': {
                    borderColor: active
                        ? tint(BRAND.orange, 0.8)
                        : SURFACE.borderHover,
                    background: active ? ACCENT.bg12 : SURFACE.hover,
                },
                '&:focus-visible': {
                    boxShadow: `0 0 0 2px ${CONTROL.focusRing}`,
                },
            }}
        >
            <Box
                sx={{
                    color: active ? meta.color : TEXT.faint,
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                {meta.icon}
            </Box>
            <Typography
                variant="caption"
                sx={{
                    fontWeight: 600,
                    color: active ? TEXT.primary : TEXT.muted,
                }}
            >
                {meta.label}
            </Typography>
            {comm.template_id ? (
                <CheckCircleIcon
                    sx={{
                        fontSize: ICON.xs,
                        color: STATUS.success.fg,
                        ml: 0.25,
                    }}
                />
            ) : (
                <Box
                    sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: TEXT.disabled,
                        ml: 0.25,
                    }}
                />
            )}
        </Box>
    )
}

/**
 * One template to pick. For WhatsApp it shows Meta's status and the sending
 * account, for email the From address. A template Meta will not send appears
 * only when it is the one assigned, and is flagged. The preview button sits
 * beside the card, not inside it, so opening a preview never selects it.
 */
const TemplateCard = ({ template, selected, onSelect, onPreview }) => {
    const meta = metaStatus(template)
    // The pipeline takes its category and sub-category from this template.
    const untagged = !template.category || !template.sub_category
    const sender = sendsAs(template.comm_type, { template })
    return (
        <Box sx={{ position: 'relative', height: '100%' }}>
            <Paper
                elevation={0}
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                aria-label={`Template ${template.template_name}`}
                aria-disabled={untagged}
                onClick={untagged ? undefined : onSelect}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        if (!untagged) onSelect()
                    }
                }}
                sx={{
                    background: selected ? ACCENT.bg08 : SURFACE.card,
                    border: selected
                        ? `1.5px solid ${BRAND.orange}`
                        : `1px solid ${SURFACE.border}`,
                    borderRadius: `${RADIUS.md}px`,
                    p: 2,
                    pb: 5,
                    cursor: untagged ? 'not-allowed' : 'pointer',
                    opacity: untagged ? 0.6 : 1,
                    outline: 'none',
                    transition: 'border-color 0.15s, background 0.15s',
                    position: 'relative',
                    height: '100%',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                        borderColor: selected
                            ? BRAND.orange
                            : SURFACE.borderHover,
                        background: selected ? ACCENT.bg12 : SURFACE.cardHover,
                    },
                    '&:focus-visible': {
                        boxShadow: `0 0 0 2px ${ACCENT.border55}`,
                    },
                }}
            >
                {selected && (
                    <CheckCircleIcon
                        sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            fontSize: ICON.md,
                            // A mark on the orange tint: the strong orange
                            // keeps 3:1 there.
                            color: ACCENT.strong,
                        }}
                    />
                )}
                <Box
                    sx={{
                        mb: 1,
                        pr: selected ? 2.5 : 0,
                        display: 'flex',
                        alignItems: 'baseline',
                        columnGap: 1.25,
                        flexWrap: 'wrap',
                    }}
                >
                    <StatusTag status={template.status} />
                    {meta && <MetaTag meta={meta} />}
                </Box>
                <Typography
                    title={template.template_name}
                    variant="body2"
                    sx={{
                        fontWeight: 600,
                        color: TEXT.primary,
                        mb: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {template.template_name}
                </Typography>
                {sender && (
                    <Typography
                        title={sender}
                        variant="caption"
                        component="p"
                        sx={{
                            color: TEXT.muted,
                            mb: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {sender}
                    </Typography>
                )}
                <Typography
                    variant="caption"
                    component="p"
                    sx={{ color: TEXT.faint, mb: 0.5 }}
                >
                    {template.created_by}
                </Typography>
                <Typography
                    variant="caption"
                    component="p"
                    sx={{
                        color: untagged ? STATUS.warning.fg : TEXT.faint,
                        mb: 1,
                    }}
                    title={
                        untagged
                            ? 'Tag this template first (Templates page); the pipeline takes its category and sub-category from it.'
                            : undefined
                    }
                >
                    {untagged
                        ? 'Not tagged: tag it before using it here'
                        : `${
                              subCategoryLabel(template.sub_category) ||
                              template.sub_category
                          } · ${template.category.charAt(0)}${template.category
                              .slice(1)
                              .toLowerCase()}`}
                </Typography>
                {meta?.tone === 'waiting' && (
                    <Typography
                        variant="caption"
                        component="p"
                        sx={{ color: STATUS.warning.fg, mb: 1 }}
                    >
                        Meta has not approved it yet: sends fail until it does.
                    </Typography>
                )}
                {meta?.tone === 'blocked' && (
                    <Typography
                        variant="caption"
                        component="p"
                        sx={{ color: STATUS.error.fg, mb: 1 }}
                    >
                        Meta will not send this template. Pick another.
                    </Typography>
                )}
                <Typography
                    variant="caption"
                    component="p"
                    sx={{ color: TEXT.faint, mt: 'auto' }}
                >
                    {moment(template.created_on * 1000).format('DD MMM YYYY')}
                </Typography>
            </Paper>
            {/* Beside the card, not inside it: a click here previews and
                never selects the template. */}
            <Box sx={{ position: 'absolute', right: 8, bottom: 8 }}>
                <IconAction
                    title="Preview"
                    label={`Preview ${template.template_name}`}
                    onClick={() => onPreview(template.id, template.comm_type)}
                    hoverColor={LINK}
                >
                    <VisibilityOutlinedIcon />
                </IconAction>
            </Box>
        </Box>
    )
}

/**
 * Step 3: assign a template to each selected channel via per-channel tabs.
 * `onPreview(templateId, commType)` opens the shared preview dialog;
 * `onRefresh` reloads the templates (`refreshing` while it does), so one made
 * in the Create a template tab can be picked here. `loading` is the first
 * load: the list says so instead of "No approved templates".
 */
const StepTemplates = ({
    form,
    templates,
    activeIdx,
    onActiveIdxChange,
    onAssignTemplate,
    onPreview,
    onRefresh,
    refreshing = false,
    loading = false,
}) => {
    // Clamp the active tab if channels were removed on the previous step.
    useEffect(() => {
        if (activeIdx >= form.comms.length) onActiveIdxChange(0)
    }, [form.comms.length, activeIdx, onActiveIdxChange])

    const [query, setQuery] = useState('')
    const activeComm = form.comms[activeIdx] ?? form.comms[0]
    const filtered = useMemo(
        () =>
            activeComm
                ? assignableTemplates(
                      templates,
                      activeComm.comm_type,
                      query,
                      activeComm.template_id
                  )
                : [],
        [templates, activeComm, query]
    )
    const shown = filtered.slice(0, MAX_SHOWN)
    const selected = activeComm?.template_id
        ? templates.find((t) => t.id === activeComm.template_id)
        : null
    const selectedName = selected?.template_name ?? ''
    const selectedRefused = metaStatus(selected)?.tone === 'blocked'
    const isWhatsApp = activeComm?.comm_type === 'whatsapp'

    return (
        <Box>
            <SectionLabel>Assign template per channel</SectionLabel>
            <Box
                role="tablist"
                aria-label="Selected channels"
                sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}
            >
                {form.comms.map((comm, idx) => (
                    <ChannelTab
                        key={comm.comm_type}
                        comm={comm}
                        active={idx === activeIdx}
                        onClick={() => onActiveIdxChange(idx)}
                    />
                ))}
            </Box>
            {activeComm && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 1,
                        mb: 1.5,
                    }}
                >
                    <Typography variant="caption" sx={{ color: TEXT.faint }}>
                        Approved {channelMeta(activeComm.comm_type).label}{' '}
                        templates
                        {isWhatsApp ? ' that Meta can send' : ''}
                        {selectedName
                            ? ` · Selected: ${selectedName}`
                            : ' · Pick one below'}
                    </Typography>
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                        <Button
                            variant="text"
                            size="small"
                            startIcon={
                                <RefreshIcon sx={{ fontSize: ICON.sm }} />
                            }
                            onClick={onRefresh}
                            disabled={refreshing}
                        >
                            {refreshing ? 'Refreshing…' : 'Refresh'}
                        </Button>
                        <Link
                            href={createTemplateHref(activeComm.comm_type)}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                typography: 'caption',
                                fontWeight: 600,
                                color: LINK,
                            }}
                        >
                            Create a template
                            <OpenInNewIcon sx={{ fontSize: ICON.sm }} />
                        </Link>
                    </Box>
                </Box>
            )}
            {selectedRefused && (
                <Typography
                    role="alert"
                    variant="caption"
                    component="p"
                    sx={{ color: STATUS.error.fg, mb: 1.5 }}
                >
                    {`The assigned template ${selectedName} cannot be sent: Meta has it as ${metaStatus(
                        selected
                    ).key.replace(/_/g, ' ')}. Pick another.`}
                </Typography>
            )}
            {activeComm && (
                <TextField
                    size="small"
                    placeholder="Search templates by name"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    inputProps={{ 'aria-label': 'Search templates' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon
                                    sx={{
                                        fontSize: ICON.md,
                                        color: TEXT.faint,
                                    }}
                                />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ mb: 2, minWidth: 280 }}
                />
            )}

            {loading && !templates.length ? (
                <InlineLoading label="Loading templates…" py={8} />
            ) : filtered.length === 0 ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 8,
                    }}
                >
                    <ArticleIcon
                        sx={{
                            fontSize: ICON.hero,
                            mb: 2,
                            color: TEXT.decorative,
                        }}
                    />
                    <Typography
                        variant="h6"
                        component="p"
                        sx={{
                            color: TEXT.muted,
                            mb: 0.5,
                        }}
                    >
                        {query.trim()
                            ? 'No approved templates match this search'
                            : 'No approved templates for this channel'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: TEXT.faint }}>
                        Only approved templates are sent; create or approve one
                        first.
                        {isWhatsApp
                            ? ' Templates Meta archived, rejected, disabled or paused are not offered.'
                            : ''}
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {shown.map((t) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={t.id}>
                            <TemplateCard
                                template={t}
                                selected={activeComm?.template_id === t.id}
                                onSelect={() => onAssignTemplate(t.id)}
                                onPreview={onPreview}
                            />
                        </Grid>
                    ))}
                    {filtered.length > shown.length && (
                        <Grid item xs={12}>
                            <Typography
                                variant="caption"
                                sx={{ color: TEXT.faint }}
                            >
                                Showing {shown.length} of {filtered.length}.
                                Search by name to find the rest.
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            )}
        </Box>
    )
}

export default StepTemplates
